<script lang="ts">
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import { get, derived } from 'svelte/store';
  import {
    friends, trips, candidates, suggestions, activeCandidateId, travelTime,
    placementMode, placementName, placementHouseholdSize, friendPlacement, continuePlacement, showToast, mapReady, mapView,
  } from '../lib/state';
  import {
    addFriend, addTripAtLocation, addCandidateAtLocation,
    friendMoved, tripMoved, candidateMoved, setActiveCandidate, promoteSuggestion,
  } from '../lib/entities';
  import { requestDeviceLocation } from '../lib/device-location';
  import { householdSize } from '../lib/household';
  import { reverseGeocode } from '../lib/api';
  import { loadGoogleMaps, createMap, setCurrentMap, getLabelOverlayClass, type LabelInstance } from '../lib/maps/map';
  import { installTouchTracker } from '../lib/maps/touch';
  import { createMarkerManager, friendIcon, tripIcon, candidateIcon, friendGlyph, TRIP_GLYPH, candidateGlyph } from '../lib/maps/markers';
  import { updateTravelOverlay, reachableFriends, reachableTrips } from '../lib/maps/overlays';
  import { currentEntityPoints, loadViewerLocation, resolveInitialMapView, viewerLocation } from '../lib/location';
  import type { Candidate, Friend, Suggestion, Trip } from '../lib/types';

  const dispatch = createEventDispatcher<{ keyError: string; candidateOpen: string }>();

  let mapEl: HTMLElement;
  let map: google.maps.Map | null = null;
  const unsubs: (() => void)[] = [];

  let friendDraftMarker: google.maps.Marker | null = null;
  let confirmingFriend = false;
  let addingPin = false;

  async function confirmFriend() {
    const point = get(friendPlacement);
    if (!point || confirmingFriend) return;
    const name = get(placementName)?.trim() || 'Friend';
    const peopleCount = householdSize(get(placementHouseholdSize));
    confirmingFriend = true;
    try {
      const address = await reverseGeocode(point.lat, point.lng);
      // Cancellation or moving the draft invalidates this pending confirmation.
      if (get(placementMode) !== 'friend' || get(friendPlacement) !== point) return;
      addFriend({ name, address, formattedAddress: address, householdSize: peopleCount, ...point });
      continuePlacement('friend');
      showToast(`Added ${name} ✓`);
    } finally {
      confirmingFriend = false;
    }
  }

  // Neighborhood suggestion outlines + a pulse traveling around each boundary.
  const SUGGEST_COLOR = '#b8861f'; // amber — the "star pick" gold
  interface Area {
    polygon: google.maps.Polygon;
    pulse: google.maps.Polyline;
    label: LabelInstance;
    icons: google.maps.IconSequence[];
  }
  let areas: Area[] = [];
  let pulseRaf = 0;
  let pulseStart = 0;
  let mapViewTimer: ReturnType<typeof setTimeout> | null = null;

  function clearAreas() {
    for (const a of areas) { a.polygon.setMap(null); a.pulse.setMap(null); a.label.setMap(null); }
    areas = [];
    if (pulseRaf) { cancelAnimationFrame(pulseRaf); pulseRaf = 0; }
    pulseStart = 0;
  }

  function syncSuggestionAreas(list: Suggestion[]) {
    if (!map) return;
    clearAreas();
    const Label = getLabelOverlayClass();
    for (const sug of list) {
      if (!sug.ring || sug.ring.length < 3) continue;
      const polygon = new google.maps.Polygon({
        map, paths: sug.ring, clickable: true, zIndex: 55,
        strokeColor: SUGGEST_COLOR, strokeOpacity: 0.9, strokeWeight: 2,
        fillColor: SUGGEST_COLOR, fillOpacity: 0.06,
      });
      const icons: google.maps.IconSequence[] = [{
        icon: {
          path: google.maps.SymbolPath.CIRCLE, scale: 5,
          fillColor: '#fff3cd', fillOpacity: 1, strokeColor: SUGGEST_COLOR, strokeWeight: 2, strokeOpacity: 1,
        },
        offset: '0%',
      }];
      // A transparent line over the boundary that carries the moving pulse dot.
      const pulse = new google.maps.Polyline({
        map, path: [...sug.ring, sug.ring[0]], strokeOpacity: 0, zIndex: 56, icons,
      });
      const text = `#${sug.rank} · ${sug.score}${sug.neighborhoodName ? ' · ' + sug.neighborhoodName : ''}`;
      const label = new Label(sug, text, 'suggestion');
      label.setMap(map);
      polygon.addListener('click', () => promoteSuggestion(sug));
      areas.push({ polygon, pulse, label, icons });
    }
    if (areas.length) pulseRaf = requestAnimationFrame(animatePulse);
  }

  function animatePulse(ts: number) {
    if (!pulseStart) pulseStart = ts;
    const t = (ts - pulseStart) / 1000;
    const offset = `${((t / 6) % 1) * 100}%`; // one lap every ~6s
    const scale = 4 + 1.8 * Math.sin(t * 4); // gentle pulse
    for (const a of areas) {
      a.icons[0].offset = offset;
      (a.icons[0].icon as google.maps.Symbol).scale = scale;
      a.pulse.set('icons', a.icons);
    }
    pulseRaf = requestAnimationFrame(animatePulse);
  }

  onMount(async () => {
    // This is the existing IP/edge lookup: no browser location permission is
    // requested. It runs even with a saved map view so recentering is available.
    void loadViewerLocation();
    try {
      await loadGoogleMaps();
      const initialView = await resolveInitialMapView();
      // Everything below can throw too (e.g. Map ctor) — keep it inside the
      // try so failures surface as a visible error, not a silent blank map.
      map = createMap(mapEl, initialView);
      setCurrentMap(map);
      // Watch finger count so pins yield to two-finger map gestures (pinch-zoom).
      installTouchTracker(mapEl);
      if (!get(mapView)) fitEntityBounds(map);
      mapReady.set(true);
      setup(map);
    } catch (e) {
      const msg = (e as Error)?.message || String(e);
      dispatch('keyError', msg === 'NO_KEY'
        ? 'No map key returned by /api/maps-key. Set MAPS_BROWSER_KEY in your Netlify env vars (or .env for local dev) and reload.'
        : 'Map failed to initialize. Check that the Maps JavaScript API is enabled and the browser key allows this domain.');
    }
  });

  function fitEntityBounds(map: google.maps.Map) {
    const points = currentEntityPoints();
    if (points.length < 2) return;
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 64);
  }

  function saveMapView(map: google.maps.Map) {
    if (mapViewTimer) clearTimeout(mapViewTimer);
    mapViewTimer = setTimeout(() => {
      const c = map.getCenter();
      const zoom = map.getZoom();
      if (!c || zoom == null) return;
      mapView.set({ lat: c.lat(), lng: c.lng(), zoom });
    }, 300);
  }

  function setup(map: google.maps.Map) {

    // Map clicks dispatch by active placement mode.
    map.addListener('click', async (e: google.maps.MapMouseEvent) => {
      if (!e.latLng || addingPin || confirmingFriend) return;
      const ll = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const mode = get(placementMode);
      if (mode === 'friend') {
        friendPlacement.set(ll);
        return;
      }
      // The mobile add sheet names the pin before placement starts; when it has
      // (even to an empty string), skip the prompt and commit straight away.
      const preset = get(placementName);
      const opts = preset === null ? undefined : { name: preset, skipPrompt: true };
      if (mode !== 'candidate' && mode !== 'trip') return;
      addingPin = true;
      try {
      const added = mode === 'candidate'
        ? await addCandidateAtLocation(ll, opts)
        : mode === 'trip'
            ? await addTripAtLocation(ll, opts)
            : false;
      if (added) continuePlacement(mode);
      } finally {
        addingPin = false;
      }
    });
    map.addListener('idle', () => saveMapView(map));
    unsubs.push(friendPlacement.subscribe((point) => {
      if (!point) {
        friendDraftMarker?.setMap(null);
        friendDraftMarker = null;
        return;
      }
      if (friendDraftMarker) {
        friendDraftMarker.setPosition(point);
      } else {
        friendDraftMarker = new google.maps.Marker({ map, position: point, draggable: true, title: 'Approximate friend location. Drag to adjust', opacity: 0.8 });
        friendDraftMarker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) friendPlacement.set({ lat: event.latLng.lat(), lng: event.latLng.lng() });
        });
      }
    }));

    // Marker managers — one per entity type, reconciled against the stores.
    // "Within reach" recolors the marker's own ring (baked into the icon), so
    // the highlight is welded to the marker and drags with it. `reach*` track
    // which ids are inside the radius and are refreshed before each re-sync.
    let reachFriends = new Set<string>();
    let reachTrips = new Set<string>();
    const friendMgr = createMarkerManager<Friend>({
      kind: 'friend', zIndex: 50, icon: (f) => friendIcon(reachFriends.has(f.id), f.householdSize), glyph: (f) => friendGlyph(f.householdSize),
      householdSize: (f) => householdSize(f.householdSize),
      labelText: (f) => householdSize(f.householdSize) > 3 ? `${f.name} · ${householdSize(f.householdSize)} people` : f.name,
      title: (f) => `${f.name} · ${householdSize(f.householdSize)} ${householdSize(f.householdSize) === 1 ? "person" : "people"}. Drag to move`, onDragEnd: friendMoved,
    });
    const tripMgr = createMarkerManager<Trip>({
      kind: 'trip', zIndex: 40, icon: (t) => tripIcon(reachTrips.has(t.id)), glyph: () => TRIP_GLYPH,
      title: (t) => `${t.name}. Drag to move`, onDragEnd: tripMoved,
    });
    const candMgr = createMarkerManager<Candidate>({
      kind: 'candidate', zIndex: 100, icon: (c) => candidateIcon(c.color), glyph: () => candidateGlyph(),
      labelColor: (c) => c.color, title: (c) => `${c.name}. Drag to move`,
      onClick: (id) => {
        setActiveCandidate(id);
        dispatch('candidateOpen', id);
      },
      onDragEnd: candidateMoved,
    });

    // Re-sync friends/trips when the list OR the reachable set changes, so the
    // ring color tracks the active radius.
    const friendInputs = derived([friends, reachableFriends], (v) => v);
    unsubs.push(friendInputs.subscribe(([list, reach]) => {
      reachFriends = new Set(reach.map((f) => f.id));
      if (map) friendMgr.sync(map, list);
    }));
    const tripInputs = derived([trips, reachableTrips], (v) => v);
    unsubs.push(tripInputs.subscribe(([list, reach]) => {
      reachTrips = new Set(reach.map((t) => t.id));
      if (map) tripMgr.sync(map, list);
    }));
    unsubs.push(candidates.subscribe((list) => map && candMgr.sync(map, list)));
    unsubs.push(suggestions.subscribe((list) => syncSuggestionAreas(list)));
    // Example loading (and imports) can intentionally request a new view after
    // the map exists. Ignore the map's own tiny idle-state updates.
    unsubs.push(mapView.subscribe((view) => {
      if (!map || !view) return;
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (!center || Math.abs(center.lat() - view.lat) > 0.0001 || Math.abs(center.lng() - view.lng) > 0.0001) {
        map.setCenter({ lat: view.lat, lng: view.lng });
      }
      if (zoom !== view.zoom) map.setZoom(view.zoom);
    }));

    // Travel-time overlay follows the active candidate + settings.
    const overlayInputs = derived([activeCandidateId, candidates, travelTime], (v) => v);
    unsubs.push(
      overlayInputs.subscribe(([activeId, cands, tt]) => {
        if (!map) return;
        const c = cands.find((x) => x.id === activeId);
        updateTravelOverlay(map, c ? { lat: c.lat, lng: c.lng } : null, tt);
      }),
    );
  }

  onDestroy(() => {
    for (const u of unsubs) u();
    clearAreas();
    friendDraftMarker?.setMap(null);
    if (mapViewTimer) clearTimeout(mapViewTimer);
  });

  function zoom(delta: number) {
    if (!map) return;
    const cur = map.getZoom() ?? 12;
    const next = Math.max(0, Math.min(22, cur + delta));
    if (next !== cur) map.setZoom(next);
  }

  // Placement banner echoes back exactly what is about to land on the map, so a
  // named pin from the mobile add sheet is confirmable before the tap.
  const PLACEMENT_GLYPH: Record<string, string> = { friend: '🙂', trip: '📍', candidate: '🏠' };
  $: placingText = $placementMode !== 'none' && $placementName
    ? `Tap the map to place ${PLACEMENT_GLYPH[$placementMode]} ${$placementName}`
    : 'Tap the map to place the pin.';

  let locating = false;
  async function centerOnViewer() {
    if (!map || locating) return;
    locating = true;
    try {
      const location = await requestDeviceLocation();
      map.setCenter({ lat: location.lat, lng: location.lng });
      map.setZoom(location.accuracy <= 1000 ? 15 : 11);
    } catch {
      const fallback = get(viewerLocation);
      if (fallback) {
        map.setCenter({ lat: fallback.lat, lng: fallback.lng });
        map.setZoom(11);
        showToast(`Device location unavailable. Showing an approximate location${fallback.city ? ` near ${fallback.city}` : ''}.`);
      } else {
        showToast('Could not find your location. Allow location access in your browser and try again.');
      }
    } finally {
      locating = false;
    }
  }

</script>

<div
  class="map-area"
  class:placement-candidate={$placementMode === 'candidate'}
  class:placement-friend={$placementMode === 'friend'}
  class:placement-trip={$placementMode === 'trip'}
>
  <div bind:this={mapEl} id="map" role="application" aria-label="Map with homes, people, and places"></div>

  {#if $placementMode !== 'none'}
    <div class="mode-banner" style="display:flex;">
      <strong class="mode-banner-heading">{$placementMode === 'friend' ? 'Adding friends' : $placementMode === 'trip' ? 'Adding places' : 'Adding homes'}</strong>
      <span class="mode-banner-text">{$placementMode === 'friend' ? ($friendPlacement ? 'Drag the pin or tap another spot. Travel times are estimated from this pin.' : 'Tap the map to choose an approximate location for your friend.') : placingText}</span>
      {#if $placementMode === 'friend' && $friendPlacement}
        <label class="friend-pin-name">Name(s)
          <input aria-label="Friend name(s)" placeholder="e.g. Sam & Naomi" value={$placementName ?? ''} on:input={(e) => placementName.set(e.currentTarget.value)} disabled={confirmingFriend} />
        </label>
        <details class="friend-pin-count">
          <summary>Household size (optional)</summary>
          <label>Number of friends <input aria-label="Number of friends at this pin" type="number" min="1" max="99" step="1" bind:value={$placementHouseholdSize} disabled={confirmingFriend} /></label>
        </details>
        <button class="mode-banner-cancel" on:click={confirmFriend} disabled={confirmingFriend}>{confirmingFriend ? 'Adding…' : 'Add friend here'}</button>
      {/if}
      <span class="mode-banner-desktop">Press Esc to exit.</span>
      <button class="mode-banner-cancel" on:click={() => placementMode.set('none')}>Done</button>
    </div>
  {/if}

  {#if $mapReady}
    <button
      class="location-control"
      on:click={centerOnViewer}
      disabled={locating}
      aria-busy={locating}
      aria-label={locating ? 'Finding your location' : 'Center on my location'}
      title={locating ? 'Finding your location…' : 'Center on my location'}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <circle cx="12" cy="12" r="4"></circle>
        <circle cx="12" cy="12" r="8"></circle>
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3"></path>
      </svg>
    </button>
  {/if}

  <div class="zoom-controls" role="group" aria-label="Zoom">
    <button class="zoom-btn" on:click={() => zoom(1)} aria-label="Zoom in">+</button>
    <button class="zoom-btn" on:click={() => zoom(-1)} aria-label="Zoom out">−</button>
  </div>

  <slot />
</div>
